import React, { forwardRef, ComponentProps, useContext } from 'react'
import {
	FloatingPosition,
	ActionIcon as MantineActionIcon,
} from '@mantine/core'
import { ClassNameValue } from 'tailwind-merge'
import cn from '../../lib/utils/cn'
import Loader from '../common/Loader'
import Tooltip from './Tooltip'
import { DangerousOutlined } from '@mui/icons-material'
import AppContext from '../../context/appContext'

type VariantType = 'primary' | 'secondary' | 'tertiary' | 'transparent'

export interface IconButtonProps
	extends Omit<ComponentProps<typeof MantineActionIcon>, 'variant'> {
	variant?: VariantType
	disabled?: boolean
	loading?: boolean
	icon?: React.ReactNode
	tooltip?: string | null
	tooltipPosition?: FloatingPosition
	tooltipOffset?: number
	className?: string
	classNameWrapper?: ClassNameValue
	loaderSize?: number
	onClick?: (e: any) => void
}

export const getVariantStyles = (variant: VariantType): ClassNameValue => {
	switch (variant) {
		case 'primary':
			return 'text-white bg-gradient-to-b from-background-gradient-start to-background-gradient-end border-none hover:bg-gradient-to-b hover:from-background-gradient-end hover:to-background-gradient-start disabled:bg-accent-disabled'
		case 'secondary':
			return 'ring-1 ring-stroke-light dark:ring-stroke-light-dark text-text-main dark:text-text-main-dark fill-text-main dark:fill-text-main-dark bg-white dark:bg-background-dark hover:bg-background-light hover:bg-background-erd hover:ring-stroke-light disabled:bg-stroke-light'
		case 'tertiary':
			return 'border border-stroke-light dark:border-stroke-light-dark  text-text-main dark:text-text-main-dark fill-text-main dark:fill-text-main-dark bg-stroke-light hover:bg-stroke-light-hover'
		case 'transparent':
			return 'bg-transparent border-none text-text-main dark:text-text-main-dark fill-text-main dark:fill-text-main-dark hover:bg-stroke-light-hover dark:hover:bg-stroke-light-dark'
		default:
			return ''
	}
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
	(
		{
			icon,
			variant = 'primary',
			className,
			classNameWrapper,
			disabled = false,
			loading = false,
			tooltip = null,
			tooltipPosition = 'top',
			tooltipOffset = 5,
			loaderSize = 16,
			onClick,
			...rest
		},
		ref
	) => {
		const { isApp } = useContext(AppContext)

		const variantStyles = getVariantStyles(variant)

		const buttonContent = loading ? (
			<Loader
				size={loaderSize}
				classNameWrapper="mx-auto"
				className={cn(
					variant === 'primary'
						? 'border-white border-b-transparent dark:border-stroke-light-dark'
						: 'border-black dark:border-white border-b-transparent dark:border-b-transparent'
				)}
			/>
		) : (
			<span className="w-full h-full flex items-center justify-center">
				{icon ?? <DangerousOutlined className="w-5 h-5 fill-negative" />}
			</span>
		)

		const button = (
			<MantineActionIcon
				ref={ref}
				className={cn(
					'rounded-lg flex items-center justify-center max-h-10 w-10 h-10 py-3 group overflow-hidden relative disabled:opacity-60 disabled:fill-stroke-light-hover dark:disabled:fill-white duration-default',
					variantStyles,
					className
				)}
				size="lg"
				disabled={disabled}
				onClick={(e) => {
					e.stopPropagation()
					if (onClick) {
						onClick(e)
					}
				}}
				{...rest}
			>
				{buttonContent}
			</MantineActionIcon>
		)

		return tooltip && !isApp ? (
			<Tooltip
				label={tooltip}
				position={tooltipPosition}
				className={cn(
					'fixed z-[100] font-normal bg-text-secondary/90 text-white mx-auto py-1.5 px-3 rounded text-xs text-wrap w-fit dark:text-text-main-dark',
					classNameWrapper
				)}
				offset={tooltipOffset}
				isButton
			>
				{button}
			</Tooltip>
		) : (
			button
		)
	}
)

IconButton.displayName = 'IconButton'

export default IconButton
